import { useState, useEffect, createContext, useContext, ReactNode } from 'react';

// Supported languages
export const languages = {
  es: { name: 'Español', flag: '🇪🇸' },
  en: { name: 'English', flag: '🇺🇸' },
  pt: { name: 'Português', flag: '🇧🇷' },
  fr: { name: 'Français', flag: '🇫🇷' },
  de: { name: 'Deutsch', flag: '🇩🇪' },
  it: { name: 'Italiano', flag: '🇮🇹' },
  zh: { name: '中文', flag: '🇨🇳' },
  ja: { name: '日本語', flag: '🇯🇵' },
  ko: { name: '한국어', flag: '🇰🇷' },
  ru: { name: 'Русский', flag: '🇷🇺' },
} as const;

export type Language = keyof typeof languages;

interface Translations {
  [key: string]: string | Translations;
}

const translations: Record<Language, Translations> = {
  es: {
    // Navigation
    chats: 'Chats',
    profile: 'Perfil',
    settings: 'Configuración',
    back: 'Volver',

    // Landing page
    welcome: 'Bienvenido a MensaG',
    secureMessaging: 'Mensajería privada con Nostr',
    connectFirst: 'Primero conéctate con Nostr para crear tu cuenta',
    privateEncryption: 'Cifrado end-to-end con NIP-17',

    // Setup
    nicknameSetup: 'Configurar tu Nick',
    nicknameRequired: 'Tu Nick (único en MensaG) *',
    nicknamePlaceholder: 'Ej: usuario123, anonimo456 (máx 29 caracteres, solo letras y números)',
    nicknameHelp: 'Este será tu identificador único y obligatorio en MensaG. Otros usuarios te encontrarán con este nick.',
    nicknameAvailable: '✓ Nick disponible',
    nicknameTaken: 'Este nick ya está en uso',
    nicknameInvalid: 'Nick inválido',
    nicknameTooLong: 'El nick debe tener máximo 29 caracteres',
    nicknameOnlyChars: 'Solo se permiten letras y números',
    continueToMensag: 'Continuar a MensaG',
    updating: 'Actualizando...',

    // Chats
    searchChats: 'Buscar conversaciones...',
    addContact: 'Añadir Contacto',
    noChats: 'No tienes conversaciones',
    noChatsDescription: 'Comienza una nueva conversación agregando un contacto por su nickname',
    addAContact: 'Agregar contacto',
    unreadMessages: 'No leídos',

    // Contacts
    findUser: 'Buscar usuario',
    enterNickname: 'Ingresa un nickname (ej: usuario123)',
    userFoundOn: 'Busca usuarios de MensaG por su nickname único',
    searching: 'Buscando...',
    userNotFound: 'Usuario no encontrado',
    userNotFoundDesc: 'Verifica el nickname e intenta de nuevo',
    asMensagUser: 'como usuario de MensaG',
    invitationMessage: 'Mensaje de invitación (opcional)',
    invitationPlaceholder: '¡Hola! Me gustaría agregarte como contacto...',
    charactersRemaining: 'caracteres restantes',
    sendRequest: 'Enviar solicitud de contacto',
    sending: 'Enviando...',

    // Contact Requests
    contactRequests: 'Solicitudes de Contacto',
    receivedRequests: 'Solicitudes Recibidas',
    sentRequests: 'Solicitudes Enviadas',
    accept: 'Aceptar',
    decline: 'Declinar',

    // Messages
    writeMessage: 'Escribe un mensaje...',
    sendMessage: 'Enviar mensaje',
    messageSent: 'Mensaje enviado',
    messageDelivered: 'Mensaje entregado',
    messageRead: 'Mensaje leído',
    messageSending: 'Enviando...',
    startChat: 'Envía el primer mensaje para iniciar el chat',
    online: 'En línea',
    offline: 'Desconectado',
    lastSeen: 'Última vez visto',

    // Profile
    profileInfo: 'Información del Perfil',
    editProfile: 'Editar Perfil',
    uniqueNickname: 'Nickname único',
    changeNick: 'Cambiar nick',
    connected: 'Conectado',
    memberSince: 'Miembro desde',
    changeNickname: 'Cambiar nickname',

    // Settings
    notifications: 'Notificaciones',
    pushNotifications: 'Notificaciones Push',
    messages: 'Mensajes',
    calls: 'Llamadas',
    groups: 'Grupos',
    silentMode: 'Modo silencioso',
    privacy: 'Privacidad',
    readReceipts: 'Confirmaciones de lectura',
    showReadConfirmations: 'Mostrar cuando leo mensajes',
    onlineStatus: 'Estado en línea',
    showWhenOnline: 'Mostrar cuando estoy conectado',
    readReceipts: 'Confirmaciones de lectura',
    onlineStatus: 'Estado en línea',
    profilePhoto: 'Foto de perfil',
    lastSeen: 'Última vez visto',
    multimedia: 'Multimedia',
    autoDownload: 'Descarga automática',
    saveToGallery: 'Guardar en galería',
    appearance: 'Apariencia',
    theme: 'Tema',

    // Themes
    defaultTheme: 'Default',
    oceanTheme: 'Ocean',
    sunsetTheme: 'Sunset',
    forestTheme: 'Forest',
    purpleTheme: 'Purple',

    // Language
    language: 'Idioma',
    selectLanguage: 'Seleccionar idioma',

    // Account
    accountActions: 'Acciones de Cuenta',
    clearHistory: 'Limpiar todo el historial',
    logout: 'Cerrar sesión',

    // App info
    poweredBy: 'Powered by Nostr',
    timings: '⏱ ⏱ ⏱',
    viewNips: 'Ver NIPs',

    // Status
    loading: 'Cargando...',
    connecting: 'Conectando...',
    connected: 'Conectado',
    error: 'Error',
    retry: 'Reintentar',
    cancel: 'Cancelar',
    save: 'Guardar',
    apply: 'Aplicar',
    yes: 'Sí',
    no: 'No',
    ok: 'Aceptar',

    // Time
    justNow: 'Ahora mismo',
    minuteAgo: 'hace 1 minuto',
    minutesAgo: 'hace {{count}} minutos',
    hourAgo: 'hace 1 hora',
    hoursAgo: 'hace {{count}} horas',
    dayAgo: 'hace 1 día',
    daysAgo: 'hace {{count}} días',

    // Voice messages
    voiceMessage: 'Mensaje de voz',
    holdToRecord: 'Mantén presionado para grabar',
    releaseToSend: 'Suelta para enviar',

    // File attachments
    attachFile: 'Adjuntar archivo',
    photo: 'Foto',
    video: 'Video',
    document: 'Documento',
    location: 'Ubicación',

    // Error messages
    somethingWentWrong: 'Algo salió mal',
    errorReported: 'El error ha sido reportado',
    reloadPage: 'Recargar página',
    networkError: 'Error de conexión',
    tryAgain: 'Inténtalo de nuevo',

    // Notifications
    requestNotificationPermission: 'Solicitar permisos de notificación',
    notificationGranted: 'Permitidas',
    notificationDenied: 'Bloqueadas',
    notificationDefault: 'No solicitadas',
    testNotification: 'Probar notificación',
    newContactRequest: 'Nueva solicitud de contacto',
    newMessage: '{{name}}: {{message}}',

    about: 'Acerca de',
    aboutDescription: 'MensaG es una aplicación de mensajería privada construida sobre el protocolo Nostr.',
    version: 'Versión',
    features: 'Características',
    secure: 'Seguro',
    private: 'Privado',
    decentralized: 'Descentralizado',
    lightning: 'Lightning',
    crypto: 'Crypto',
  },
  en: {
    // Navigation
    chats: 'Chats',
    profile: 'Profile',
    settings: 'Settings',
    back: 'Back',

    // Landing page
    welcome: 'Welcome to MensaG',
    secureMessaging: 'Private messaging with Nostr',
    connectFirst: 'First connect with Nostr to create your account',
    privateEncryption: 'End-to-end encryption with NIP-17',

    // Setup
    nicknameSetup: 'Set up your Nick',
    nicknameRequired: 'Your Nick (unique on MensaG) *',
    nicknamePlaceholder: 'Ex: user123, anonymous456 (max 29 chars, letters & numbers only)',
    nicknameHelp: 'This will be your unique identifier on MensaG. Other users will find you with this nick.',
    nicknameAvailable: '✓ Nick available',
    nicknameTaken: 'This nick is already taken',
    nicknameInvalid: 'Invalid nick',
    nicknameTooLong: 'Nick must be maximum 29 characters',
    nicknameOnlyChars: 'Only letters and numbers allowed',
    continueToMensag: 'Continue to MensaG',
    updating: 'Updating...',

    // Chats
    searchChats: 'Search conversations...',
    addContact: 'Add Contact',
    noChats: 'You have no conversations',
    noChatsDescription: 'Start a new conversation by adding a contact using their nickname',
    addAContact: 'Add a contact',
    unreadMessages: 'Unread',

    // Contacts
    findUser: 'Find user',
    enterNickname: 'Enter a nickname (ex: user123)',
    userFoundOn: 'Search for MensaG users by their unique nickname',
    searching: 'Searching...',
    userNotFound: 'User not found',
    userNotFoundDesc: 'Check the nickname and try again',
    asMensagUser: 'as a MensaG user',
    invitationMessage: 'Invitation message (optional)',
    invitationPlaceholder: 'Hi! I would like to add you as a contact...',
    charactersRemaining: 'characters remaining',
    sendRequest: 'Send contact request',
    sending: 'Sending...',

    // Contact Requests
    contactRequests: 'Contact Requests',
    receivedRequests: 'Received Requests',
    sentRequests: 'Sent Requests',
    accept: 'Accept',
    decline: 'Decline',

    // Messages
    writeMessage: 'Write a message...',
    sendMessage: 'Send message',
    messageSent: 'Message sent',
    messageDelivered: 'Message delivered',
    messageRead: 'Message read',
    messageSending: 'Sending...',
    startChat: 'Send the first message to start the chat',
    online: 'Online',
    offline: 'Offline',
    lastSeen: 'Last seen',

    // Profile
    profileInfo: 'Profile Information',
    editProfile: 'Edit Profile',
    uniqueNickname: 'Unique nickname',
    changeNick: 'Change nick',
    connected: 'Connected',
    memberSince: 'Member since',
    changeNickname: 'Change nickname',

    // Settings
    notifications: 'Notifications',
    pushNotifications: 'Push Notifications',
    messages: 'Messages',
    calls: 'Calls',
    groups: 'Groups',
    silentMode: 'Silent mode',
    privacy: 'Privacy',
    readReceipts: 'Read receipts',
    showReadConfirmations: 'Show when I read messages',
    onlineStatus: 'Online status',
    showWhenOnline: 'Show when I am online',
    profilePhoto: 'Profile photo',
    lastSeen: 'Last seen',
    multimedia: 'Multimedia',
    autoDownload: 'Auto download',
    saveToGallery: 'Save to gallery',
    appearance: 'Appearance',
    theme: 'Theme',

    // Themes
    defaultTheme: 'Default',
    oceanTheme: 'Ocean',
    sunsetTheme: 'Sunset',
    forestTheme: 'Forest',
    purpleTheme: 'Purple',

    // Language
    language: 'Language',
    selectLanguage: 'Select language',

    // Account
    accountActions: 'Account Actions',
    clearHistory: 'Clear all history',
    logout: 'Log out',

    // App info
    poweredBy: 'Powered by Nostr',
    timings: '⏱ ⏱ ⏱',
    viewNips: 'View NIPs',

    // Status
    loading: 'Loading...',
    connecting: 'Connecting...',
    connected: 'Connected',
    error: 'Error',
    retry: 'Retry',
    cancel: 'Cancel',
    save: 'Save',
    apply: 'Apply',
    yes: 'Yes',
    no: 'No',
    ok: 'OK',

    // Time
    justNow: 'Just now',
    minuteAgo: '1 minute ago',
    minutesAgo: '{{count}} minutes ago',
    hourAgo: '1 hour ago',
    hoursAgo: '{{count}} hours ago',
    dayAgo: '1 day ago',
    daysAgo: '{{count}} days ago',

    // Voice messages
    voiceMessage: 'Voice message',
    holdToRecord: 'Hold to record',
    releaseToSend: 'Release to send',

    // File attachments
    attachFile: 'Attach file',
    photo: 'Photo',
    video: 'Video',
    document: 'Document',
    location: 'Location',

    // Error messages
    somethingWentWrong: 'Something went wrong',
    errorReported: 'The error has been reported',
    reloadPage: 'Reload page',
    networkError: 'Network error',
    tryAgain: 'Try again',

    // Notifications
    requestNotificationPermission: 'Request notification permissions',
    notificationGranted: 'Granted',
    notificationDenied: 'Blocked',
    notificationDefault: 'Not requested',
    testNotification: 'Test notification',
    newContactRequest: 'New contact request',
    newMessage: '{{name}}: {{message}}',

    about: 'About',
    aboutDescription: 'MensaG is a private messaging application built on the Nostr protocol.',
    version: 'Version',
    features: 'Features',
    secure: 'Secure',
    private: 'Private',
    decentralized: 'Decentralized',
    lightning: 'Lightning',
    crypto: 'Crypto',
  },
  pt: {
    // Navigation
    chats: 'Chats',
    profile: 'Perfil',
    settings: 'Configurações',
    back: 'Voltar',

    // Landing page
    welcome: 'Bem-vindo ao MensaG',
    secureMessaging: 'Mensagens privadas com Nostr',
    connectFirst: 'Primeiro conecte-se com Nostr para criar sua conta',
    privateEncryption: 'Criptografia end-to-end com NIP-17',

    // Setup
    nicknameSetup: 'Configure seu Nick',
    nicknameRequired: 'Seu Nick (único no MensaG) *',
    nicknamePlaceholder: 'Ex: usuario123, anonimo456 (máx 29 caracteres, apenas letras e números)',
    nicknameHelp: 'Este será seu identificador único no MensaG. Outros usuários encontrarão você com este nick.',
    nicknameAvailable: '✓ Nick disponível',
    nicknameTaken: 'Este nick já está em uso',
    nicknameInvalid: 'Nick inválido',
    nicknameTooLong: 'O nick deve ter no máximo 29 caracteres',
    nicknameOnlyChars: 'Apenas letras e números são permitidos',
    continueToMensag: 'Continuar para MensaG',
    updating: 'Atualizando...',

    // Chats
    searchChats: 'Buscar conversas...',
    addContact: 'Adicionar Contato',
    noChats: 'Você não tem conversas',
    noChatsDescription: 'Inicie uma nova conversa adicionando um contato usando seu nickname',
    addAContact: 'Adicionar um contato',
    unreadMessages: 'Não lidos',

    // Contacts
    findUser: 'Encontrar usuário',
    enterNickname: 'Digite um nickname (ex: usuario123)',
    userFoundOn: 'Pesquise usuários MensaG por seu nickname único',
    searching: 'Pesquisando...',
    userNotFound: 'Usuário não encontrado',
    userNotFoundDesc: 'Verifique o nickname e tente novamente',
    asMensagUser: 'como usuário do MensaG',
    invitationMessage: 'Mensagem de convite (opcional)',
    invitationPlaceholder: 'Oi! Eu gostaria de adicioná-lo como contato...',
    charactersRemaining: 'caracteres restantes',
    sendRequest: 'Enviar solicitação de contato',
    sending: 'Enviando...',

    // Contact Requests
    contactRequests: 'Solicitações de Contato',
    receivedRequests: 'Solicitações Recebidas',
    sentRequests: 'Solicitações Enviadas',
    accept: 'Aceitar',
    decline: 'Recusar',

    // Messages
    writeMessage: 'Escreva uma mensagem...',
    sendMessage: 'Enviar mensagem',
    messageSent: 'Mensagem enviada',
    messageDelivered: 'Mensagem entregue',
    messageRead: 'Mensagem lida',
    messageSending: 'Enviando...',
    startChat: 'Envie a primeira mensagem para iniciar o chat',
    online: 'Online',
    offline: 'Offline',
    lastSeen: 'Última vez visto',

    // Profile
    profileInfo: 'Informações do Perfil',
    editProfile: 'Editar Perfil',
    uniqueNickname: 'Nickname único',
    changeNick: 'Alterar nick',
    connected: 'Conectado',
    memberSince: 'Membro desde',
    changeNickname: 'Alterar nickname',

    // Settings
    notifications: 'Notificações',
    pushNotifications: 'Notificações Push',
    messages: 'Mensagens',
    calls: 'Chamadas',
    groups: 'Grupos',
    silentMode: 'Modo silencioso',
    privacy: 'Privacidade',
    readReceipts: 'Confirmações de leitura',
    showReadConfirmations: 'Mostrar quando leio mensagens',
    onlineStatus: 'Status online',
    showWhenOnline: 'Mostrar quando estou online',
    profilePhoto: 'Foto do perfil',
    lastSeen: 'Última vez visto',
    multimedia: 'Multimídia',
    autoDownload: 'Download automático',
    saveToGallery: 'Salvar na galeria',
    appearance: 'Aparência',
    theme: 'Tema',

    // Themes
    defaultTheme: 'Padrão',
    oceanTheme: 'Oceano',
    sunsetTheme: 'Pôr do Sol',
    forestTheme: 'Floresta',
    purpleTheme: 'Roxo',

    // Language
    language: 'Idioma',
    selectLanguage: 'Selecionar idioma',

    // Account
    accountActions: 'Ações da Conta',
    clearHistory: 'Limpar todo o histórico',
    logout: 'Sair',

    // App info
    poweredBy: 'Desenvolvido por Nostr',
    timings: '⏱ ⏱ ⏱',
    viewNips: 'Ver NIPs',

    // Status
    loading: 'Carregando...',
    connecting: 'Conectando...',
    connected: 'Conectado',
    error: 'Erro',
    retry: 'Tentar novamente',
    cancel: 'Cancelar',
    save: 'Salvar',
    apply: 'Aplicar',
    yes: 'Sim',
    no: 'Não',
    ok: 'OK',

    // Time
    justNow: 'Agora mesmo',
    minuteAgo: 'há 1 minuto',
    minutesAgo: 'há {{count}} minutos',
    hourAgo: 'há 1 hora',
    hoursAgo: 'há {{count}} horas',
    dayAgo: 'há 1 dia',
    daysAgo: 'há {{count}} dias',

    // Voice messages
    voiceMessage: 'Mensagem de voz',
    holdToRecord: 'Pressione e segure para gravar',
    releaseToSend: 'Solte para enviar',

    // File attachments
    attachFile: 'Anexar arquivo',
    photo: 'Foto',
    video: 'Vídeo',
    document: 'Documento',
    location: 'Localização',

    // Error messages
    somethingWentWrong: 'Algo deu errado',
    errorReported: 'O erro foi reportado',
    reloadPage: 'Recarregar página',
    networkError: 'Erro de rede',
    tryAgain: 'Tente novamente',

    // Notifications
    requestNotificationPermission: 'Solicitar permissões de notificação',
    notificationGranted: 'Permitidas',
    notificationDenied: 'Bloqueadas',
    notificationDefault: 'Não solicitadas',
    testNotification: 'Testar notificação',
    newContactRequest: 'Nova solicitação de contato',
    newMessage: '{{name}}: {{message}}',

    about: 'Sobre',
    aboutDescription: 'MensaG é um aplicativo de mensagens privadas construído sobre o protocolo Nostr.',
    version: 'Versão',
    features: 'Recursos',
    secure: 'Seguro',
    private: 'Privado',
    decentralized: 'Descentralizado',
    lightning: 'Lightning',
    crypto: 'Crypto',
  },
  fr: {
    // Navigation
    chats: 'Chats',
    profile: 'Profil',
    settings: 'Paramètres',
    back: 'Retour',

    // Landing page
    welcome: 'Bienvenue sur MensaG',
    secureMessaging: 'Messagerie privée avec Nostr',
    connectFirst: 'Connectez-vous d\'abord avec Nostr pour créer votre compte',
    privateEncryption: 'Chiffrement de bout en bout avec NIP-17',

    // Setup
    nicknameSetup: 'Configurer votre Nick',
    nicknameRequired: 'Votre Nick (unique sur MensaG) *',
    nicknamePlaceholder: 'Ex: utilisateur123, anonyme456 (max 29 caractères, lettres et chiffres seulement)',
    nicknameHelp: 'Ce sera votre identifiant unique sur MensaG. Les autres utilisateurs vous trouveront avec ce nick.',
    nicknameAvailable: '✓ Nick disponible',
    nicknameTaken: 'Ce nick est déjà pris',
    nicknameInvalid: 'Nick invalide',
    nicknameTooLong: 'Le nick doit faire maximum 29 caractères',
    nicknameOnlyChars: 'Seulement les lettres et chiffres sont autorisés',
    continueToMensag: 'Continuer vers MensaG',
    updating: 'Mise à jour...',

    // Chats
    searchChats: 'Rechercher des conversations...',
    addContact: 'Ajouter un Contact',
    noChats: 'Vous n\'avez pas de conversations',
    noChatsDescription: 'Commencez une nouvelle conversation en ajoutant un contact en utilisant son nickname',
    addAContact: 'Ajouter un contact',
    unreadMessages: 'Non lus',

    // Contacts
    findUser: 'Trouver un utilisateur',
    enterNickname: 'Entrez un nickname (ex: utilisateur123)',
    userFoundOn: 'Rechercher des utilisateurs MensaG par leur nickname unique',
    searching: 'Recherche...',
    userNotFound: 'Utilisateur non trouvé',
    userNotFoundDesc: 'Vérifiez le nickname et réessayez',
    asMensagUser: 'comme utilisateur MensaG',
    invitationMessage: 'Message d\'invitation (optionnel)',
    invitationPlaceholder: 'Salut! Je voudrais vous ajouter comme contact...',
    charactersRemaining: 'caractères restants',
    sendRequest: 'Envoyer une demande de contact',
    sending: 'Envoi...',

    // Contact Requests
    contactRequests: 'Demandes de Contact',
    receivedRequests: 'Demandes Reçues',
    sentRequests: 'Demandes Envoyées',
    accept: 'Accepter',
    decline: 'Refuser',

    // Messages
    writeMessage: 'Écrivez un message...',
    sendMessage: 'Envoyer le message',
    messageSent: 'Message envoyé',
    messageDelivered: 'Message délivré',
    messageRead: 'Message lu',
    messageSending: 'Envoi...',
    startChat: 'Envoyez le premier message pour commencer le chat',
    online: 'En ligne',
    offline: 'Hors ligne',
    lastSeen: 'Dernière fois vu',

    // Profile
    profileInfo: 'Informations du Profil',
    editProfile: 'Modifier le Profil',
    uniqueNickname: 'Nickname unique',
    changeNick: 'Changer de nick',
    connected: 'Connecté',
    memberSince: 'Membre depuis',
    changeNickname: 'Changer de nickname',

    // Settings
    notifications: 'Notifications',
    pushNotifications: 'Notifications Push',
    messages: 'Messages',
    calls: 'Appels',
    groups: 'Groupes',
    silentMode: 'Mode silencieux',
    privacy: 'Confidentialité',
    readReceipts: 'Accusés de lecture',
    showReadConfirmations: 'Afficher quand je lis les messages',
    onlineStatus: 'Statut en ligne',
    showWhenOnline: 'Afficher quand je suis en ligne',
    profilePhoto: 'Photo de profil',
    lastSeen: 'Dernière fois vu',
    multimedia: 'Multimédia',
    autoDownload: 'Téléchargement automatique',
    saveToGallery: 'Sauvegarder dans la galerie',
    appearance: 'Apparence',
    theme: 'Thème',

    // Themes
    defaultTheme: 'Défaut',
    oceanTheme: 'Océan',
    sunsetTheme: 'Coucher du soleil',
    forestTheme: 'Forêt',
    purpleTheme: 'Violet',

    // Language
    language: 'Langue',
    selectLanguage: 'Sélectionner la langue',

    // Account
    accountActions: 'Actions du Compte',
    clearHistory: 'Effacer tout l\'historique',
    logout: 'Se déconnecter',

    // App info
    poweredBy: 'Propulsé par Nostr',
    timings: '⏱ ⏱ ⏱',
    viewNips: 'Voir les NIPs',

    // Status
    loading: 'Chargement...',
    connecting: 'Connexion...',
    connected: 'Connecté',
    error: 'Erreur',
    retry: 'Réessayer',
    cancel: 'Annuler',
    save: 'Sauvegarder',
    apply: 'Appliquer',
    yes: 'Oui',
    no: 'Non',
    ok: 'OK',

    // Time
    justNow: 'À l\'instant',
    minuteAgo: 'il y a 1 minute',
    minutesAgo: 'il y a {{count}} minutes',
    hourAgo: 'il y a 1 heure',
    hoursAgo: 'il y a {{count}} heures',
    dayAgo: 'il y a 1 jour',
    daysAgo: 'il y a {{count}} jours',

    // Voice messages
    voiceMessage: 'Message vocal',
    holdToRecord: 'Maintenir appuyé pour enregistrer',
    releaseToSend: 'Relâcher pour envoyer',

    // File attachments
    attachFile: 'Joindre un fichier',
    photo: 'Photo',
    video: 'Vidéo',
    document: 'Document',
    location: 'Localisation',

    // Error messages
    somethingWentWrong: 'Quelque chose s\'est mal passé',
    errorReported: 'L\'erreur a été signalée',
    reloadPage: 'Recharger la page',
    networkError: 'Erreur de réseau',
    tryAgain: 'Réessayez',

    // Notifications
    requestNotificationPermission: 'Demander les autorisations de notification',
    notificationGranted: 'Autorisées',
    notificationDenied: 'Bloquées',
    notificationDefault: 'Non demandées',
    testNotification: 'Tester la notification',
    newContactRequest: 'Nouvelle demande de contact',
    newMessage: '{{name}}: {{message}}',

    about: 'À propos',
    aboutDescription: 'MensaG est une application de messagerie privée construite sur le protocole Nostr.',
    version: 'Version',
    features: 'Fonctionnalités',
    secure: 'Sécurisé',
    private: 'Privé',
    decentralized: 'Décentralisé',
    lightning: 'Lightning',
    crypto: 'Crypto',
  },
  de: {
    // Navigation
    chats: 'Chats',
    profile: 'Profil',
    settings: 'Einstellungen',
    back: 'Zurück',

    // Landing page
    welcome: 'Willkommen bei MensaG',
    secureMessaging: 'Private Messaging mit Nostr',
    connectFirst: 'Verbinden Sie sich zuerst mit Nostr, um Ihr Konto zu erstellen',
    privateEncryption: 'End-to-End-Verschlüsselung mit NIP-17',

    // Setup
    nicknameSetup: 'Richten Sie Ihren Nick ein',
    nicknameRequired: 'Ihr Nick (einzigartig auf MensaG) *',
    nicknamePlaceholder: 'Bsp: benutzer123, anonym456 (max 29 Zeichen, nur Buchstaben & Zahlen)',
    nicknameHelp: 'Dies wird Ihre einzigartige Kennung auf MensaG sein. Andere Benutzer finden Sie mit diesem Nick.',
    nicknameAvailable: '✓ Nick verfügbar',
    nicknameTaken: 'Dieser Nick ist bereits vergeben',
    nicknameInvalid: 'Ungültiger Nick',
    nicknameTooLong: 'Nick darf maximal 29 Zeichen lang sein',
    nicknameOnlyChars: 'Nur Buchstaben und Zahlen erlaubt',
    continueToMensag: 'Weiter zu MensaG',
    updating: 'Aktualisierung...',

    // Chats
    searchChats: 'Konversationen suchen...',
    addContact: 'Kontakt hinzufügen',
    noChats: 'Sie haben keine Konversationen',
    noChatsDescription: 'Starten Sie eine neue Konversation, indem Sie einen Kontakt über seinen Nickname hinzufügen',
    addAContact: 'Einen Kontakt hinzufügen',
    unreadMessages: 'Ungelesen',

    // Contacts
    findUser: 'Benutzer finden',
    enterNickname: 'Geben Sie einen Nickname ein (z.B.: benutzer123)',
    userFoundOn: 'Suchen Sie nach MensaG-Benutzern über ihren einzigartigen Nickname',
    searching: 'Suche...',
    userNotFound: 'Benutzer nicht gefunden',
    userNotFoundDesc: 'Überprüfen Sie den Nickname und versuchen Sie es erneut',
    asMensagUser: 'als MensaG-Benutzer',
    invitationMessage: 'Einladungsnachricht (optional)',
    invitationPlaceholder: 'Hallo! Ich möchte Sie als Kontakt hinzufügen...',
    charactersRemaining: 'Zeichen übrig',
    sendRequest: 'Kontaktanfrage senden',
    sending: 'Wird gesendet...',

    // Contact Requests
    contactRequests: 'Kontaktanfragen',
    receivedRequests: 'Erhaltene Anfragen',
    sentRequests: 'Gesendete Anfragen',
    accept: 'Akzeptieren',
    decline: 'Ablehnen',

    // Messages
    writeMessage: 'Schreiben Sie eine Nachricht...',
    sendMessage: 'Nachricht senden',
    messageSent: 'Nachricht gesendet',
    messageDelivered: 'Nachricht zugestellt',
    messageRead: 'Nachricht gelesen',
    messageSending: 'Wird gesendet...',
    startChat: 'Senden Sie die erste Nachricht, um den Chat zu starten',
    online: 'Online',
    offline: 'Offline',
    lastSeen: 'Zuletzt gesehen',

    // Profile
    profileInfo: 'Profilinformationen',
    editProfile: 'Profil bearbeiten',
    uniqueNickname: 'Einzigartiger Nickname',
    changeNick: 'Nick ändern',
    connected: 'Verbunden',
    memberSince: 'Mitglied seit',
    changeNickname: 'Nickname ändern',

    // Settings
    notifications: 'Benachrichtigungen',
    pushNotifications: 'Push-Benachrichtigungen',
    messages: 'Nachrichten',
    calls: 'Anrufe',
    groups: 'Gruppen',
    silentMode: 'Stiller Modus',
    privacy: 'Datenschutz',
    readReceipts: 'Lesebestätigungen',
    showReadConfirmations: 'Zeigen, wenn ich Nachrichten lese',
    onlineStatus: 'Online-Status',
    showWhenOnline: 'Zeigen, wenn ich online bin',
    profilePhoto: 'Profilfoto',
    lastSeen: 'Zuletzt gesehen',
    multimedia: 'Multimedia',
    autoDownload: 'Automatisches Herunterladen',
    saveToGallery: 'In Galerie speichern',
    appearance: 'Erscheinungsbild',
    theme: 'Thema',

    // Themes
    defaultTheme: 'Standard',
    oceanTheme: 'Ozean',
    sunsetTheme: 'Sonnenuntergang',
    forestTheme: 'Wald',
    purpleTheme: 'Lila',

    // Language
    language: 'Sprache',
    selectLanguage: 'Sprache auswählen',

    // Account
    accountActions: 'Kontoaktionen',
    clearHistory: 'Gesamten Verlauf löschen',
    logout: 'Abmelden',

    // App info
    poweredBy: 'Unterstützt von Nostr',
    timings: '⏱ ⏱ ⏱',
    viewNips: 'NIPs anzeigen',

    // Status
    loading: 'Lädt...',
    connecting: 'Verbindet...',
    connected: 'Verbunden',
    error: 'Fehler',
    retry: 'Erneut versuchen',
    cancel: 'Abbrechen',
    save: 'Speichern',
    apply: 'Anwenden',
    yes: 'Ja',
    no: 'Nein',
    ok: 'OK',

    // Time
    justNow: 'Gerade eben',
    minuteAgo: 'vor 1 Minute',
    minutesAgo: 'vor {{count}} Minuten',
    hourAgo: 'vor 1 Stunde',
    hoursAgo: 'vor {{count}} Stunden',
    dayAgo: 'vor 1 Tag',
    daysAgo: 'vor {{count}} Tagen',

    // Voice messages
    voiceMessage: 'Sprachnachricht',
    holdToRecord: 'Halten zum Aufzeichnen',
    releaseToSend: 'Loslassen zum Senden',

    // File attachments
    attachFile: 'Datei anhängen',
    photo: 'Foto',
    video: 'Video',
    document: 'Dokument',
    location: 'Standort',

    // Error messages
    somethingWentWrong: 'Etwas ist schiefgelaufen',
    errorReported: 'Der Fehler wurde gemeldet',
    reloadPage: 'Seite neu laden',
    networkError: 'Netzwerkfehler',
    tryAgain: 'Erneut versuchen',

    // Notifications
    requestNotificationPermission: 'Benachrichtigungsberechtigungen anfordern',
    notificationGranted: 'Erteilt',
    notificationDenied: 'Blockiert',
    notificationDefault: 'Nicht angefordert',
    testNotification: 'Benachrichtigung testen',
    newContactRequest: 'Neue Kontaktanfrage',
    newMessage: '{{name}}: {{message}}',

    about: 'Über',
    aboutDescription: 'MensaG ist eine private Messaging-Anwendung, die auf dem Nostr-Protokoll basiert.',
    version: 'Version',
    features: 'Funktionen',
    secure: 'Sicher',
    private: 'Privat',
    decentralized: 'Dezentralisiert',
    lightning: 'Lightning',
    crypto: 'Crypto',
  },
  it: {
    // Navigation
    chats: 'Chat',
    profile: 'Profilo',
    settings: 'Impostazioni',
    back: 'Indietro',

    // Landing page
    welcome: 'Benvenuto in MensaG',
    secureMessaging: 'Messaggistica privata con Nostr',
    connectFirst: 'Prima connettiti con Nostr per creare il tuo account',
    privateEncryption: 'Crittografia end-to-end con NIP-17',

    // Setup
    nicknameSetup: 'Imposta il tuo Nick',
    nicknameRequired: 'Il tuo Nick (unico su MensaG) *',
    nicknamePlaceholder: 'Es: utente123, anonimo456 (max 29 caratteri, solo lettere e numeri)',
    nicknameHelp: 'Questo sarà il tuo identificatore unico su MensaG. Altri utenti ti troveranno con questo nick.',
    nicknameAvailable: '✓ Nick disponibile',
    nicknameTaken: 'Questo nick è già in uso',
    nicknameInvalid: 'Nick non valido',
    nicknameTooLong: 'Il nick deve essere massimo 29 caratteri',
    nicknameOnlyChars: 'Sono ammessi solo lettere e numeri',
    continueToMensag: 'Continua su MensaG',
    updating: 'Aggiornamento...',

    // Chats
    searchChats: 'Cerca conversazioni...',
    addContact: 'Aggiungi Contatto',
    noChats: 'Non hai conversazioni',
    noChatsDescription: 'Inizia una nuova conversazione aggiungendo un contatto usando il suo nickname',
    addAContact: 'Aggiungi un contatto',
    unreadMessages: 'Non lette',

    // Contacts
    findUser: 'Trova utente',
    enterNickname: 'Inserisci un nickname (es: utente123)',
    userFoundOn: 'Cerca utenti MensaG tramite il loro nickname unico',
    searching: 'Ricerca...',
    userNotFound: 'Utente non trovato',
    userNotFoundDesc: 'Verifica il nickname e riprova',
    asMensagUser: 'come utente MensaG',
    invitationMessage: 'Messaggio di invito (opzionale)',
    invitationPlaceholder: 'Ciao! Vorrei aggiungerti come contatto...',
    charactersRemaining: 'caratteri rimanenti',
    sendRequest: 'Invia richiesta di contatto',
    sending: 'Invio...',

    // Contact Requests
    contactRequests: 'Richieste di Contatto',
    receivedRequests: 'Richieste Ricevute',
    sentRequests: 'Richieste Inviate',
    accept: 'Accetta',
    decline: 'Rifiuta',

    // Messages
    writeMessage: 'Scrivi un messaggio...',
    sendMessage: 'Invia messaggio',
    messageSent: 'Messaggio inviato',
    messageDelivered: 'Messaggio consegnato',
    messageRead: 'Messaggio letto',
    messageSending: 'Invio...',
    startChat: 'Invia il primo messaggio per iniziare la chat',
    online: 'Online',
    offline: 'Offline',
    lastSeen: 'Ultima volta visto',

    // Profile
    profileInfo: 'Informazioni del Profilo',
    editProfile: 'Modifica Profilo',
    uniqueNickname: 'Nickname unico',
    changeNick: 'Cambia nick',
    connected: 'Connesso',
    memberSince: 'Membro dal',
    changeNickname: 'Cambia nickname',

    // Settings
    notifications: 'Notifiche',
    pushNotifications: 'Notifiche Push',
    messages: 'Messaggi',
    calls: 'Chiamate',
    groups: 'Gruppi',
    silentMode: 'Modalità silenziosa',
    privacy: 'Privacy',
    readReceipts: 'Conferme di lettura',
    showReadConfirmations: 'Mostra quando leggo i messaggi',
    onlineStatus: 'Stato online',
    showWhenOnline: 'Mostra quando sono online',
    profilePhoto: 'Foto profilo',
    lastSeen: 'Ultima volta visto',
    multimedia: 'Multimedia',
    autoDownload: 'Download automatico',
    saveToGallery: 'Salva nella galleria',
    appearance: 'Aspetto',
    theme: 'Tema',

    // Themes
    defaultTheme: 'Predefinito',
    oceanTheme: 'Oceano',
    sunsetTheme: 'Tramonto',
    forestTheme: 'Foresta',
    purpleTheme: 'Viola',

    // Language
    language: 'Lingua',
    selectLanguage: 'Seleziona lingua',

    // Account
    accountActions: 'Azioni Account',
    clearHistory: 'Cancella tutta la cronologia',
    logout: 'Disconnetti',

    // App info
    poweredBy: 'Realizzato con Nostr',
    timings: '⏱ ⏱ ⏱',
    viewNips: 'Visualizza NIPs',

    // Status
    loading: 'Caricamento...',
    connecting: 'Connessione...',
    connected: 'Connesso',
    error: 'Errore',
    retry: 'Riprova',
    cancel: 'Annulla',
    save: 'Salva',
    apply: 'Applica',
    yes: 'Sì',
    no: 'No',
    ok: 'OK',

    // Time
    justNow: 'Proprio ora',
    minuteAgo: '1 minuto fa',
    minutesAgo: '{{count}} minuti fa',
    hourAgo: '1 ora fa',
    hoursAgo: '{{count}} ore fa',
    dayAgo: '1 giorno fa',
    daysAgo: '{{count}} giorni fa',

    // Voice messages
    voiceMessage: 'Messaggio vocale',
    holdToRecord: 'Tieni premuto per registrare',
    releaseToSend: 'Rilascia per inviare',

    // File attachments
    attachFile: 'Allega file',
    photo: 'Foto',
    video: 'Video',
    document: 'Documento',
    location: 'Posizione',

    // Error messages
    somethingWentWrong: 'Qualcosa è andato storto',
    errorReported: 'L\'errore è stato segnalato',
    reloadPage: 'Ricarica pagina',
    networkError: 'Errore di rete',
    tryAgain: 'Riprova',

    // Notifications
    requestNotificationPermission: 'Richiedi permessi di notifica',
    notificationGranted: 'Concesse',
    notificationDenied: 'Bloccate',
    notificationDefault: 'Non richieste',
    testNotification: 'Test notifica',
    newContactRequest: 'Nuova richiesta di contatto',
    newMessage: '{{name}}: {{message}}',

    about: 'Info',
    aboutDescription: 'MensaG è un\'applicazione di messaggistica privata basata sul protocollo Nostr.',
    version: 'Versione',
    features: 'Caratteristiche',
    secure: 'Sicuro',
    private: 'Privato',
    decentralized: 'Decentralizzato',
    lightning: 'Lightning',
    crypto: 'Crypto',
  },
  zh: {
    // Navigation
    chats: '聊天',
    profile: '个人资料',
    settings: '设置',
    back: '返回',

    // Landing page
    welcome: '欢迎来到 MensaG',
    secureMessaging: '使用 Nostr 的私密消息',
    connectFirst: '首先连接 Nostr 来创建您的账户',
    privateEncryption: '使用 NIP-17 的端到端加密',

    // Setup
    nicknameSetup: '设置您的昵称',
    nicknameRequired: '您的昵称 (在 MensaG 上独一无二) *',
    nicknamePlaceholder: '例: user123, anonymous456 (最多 29 个字符，仅限字母和数字)',
    nicknameHelp: '这将是您在 MensaG 上的唯一标识符。其他用户将使用此昵称找到您。',
    nicknameAvailable: '✓ 昵称可用',
    nicknameTaken: '此昵称已被使用',
    nicknameInvalid: '无效昵称',
    nicknameTooLong: '昵称最多 29 个字符',
    nicknameOnlyChars: '仅允许字母和数字',
    continueToMensag: '继续使用 MensaG',
    updating: '更新中...',

    // Chats
    searchChats: '搜索对话...',
    addContact: '添加联系人',
    noChats: '您没有对话',
    noChatsDescription: '通过添加联系人并使用他们的昵称开始新对话',
    addAContact: '添加联系人',
    unreadMessages: '未读',

    // Contacts
    findUser: '查找用户',
    enterNickname: '输入昵称 (例: user123)',
    userFoundOn: '通过他们的唯一昵称搜索 MensaG 用户',
    searching: '搜索中...',
    userNotFound: '未找到用户',
    userNotFoundDesc: '检查昵称并重试',
    asMensagUser: '作为 MensaG 用户',
    invitationMessage: '邀请消息 (可选)',
    invitationPlaceholder: '你好！我希望将您添加为联系人...',
    charactersRemaining: '剩余字符',
    sendRequest: '发送联系人请求',
    sending: '发送中...',

    // Contact Requests
    contactRequests: '联系人请求',
    receivedRequests: '收到的请求',
    sentRequests: '发送的请求',
    accept: '接受',
    decline: '拒绝',

    // Messages
    writeMessage: '写消息...',
    sendMessage: '发送消息',
    messageSent: '消息已发送',
    messageDelivered: '消息已送达',
    messageRead: '消息已读',
    messageSending: '发送中...',
    startChat: '发送第一条消息以开始聊天',
    online: '在线',
    offline: '离线',
    lastSeen: '最后一次见到',

    // Profile
    profileInfo: '个人资料信息',
    editProfile: '编辑个人资料',
    uniqueNickname: '唯一昵称',
    changeNick: '更改昵称',
    connected: '已连接',
    memberSince: '会员自',
    changeNickname: '更改昵称',

    // Settings
    notifications: '通知',
    pushNotifications: '推送通知',
    messages: '消息',
    calls: '通话',
    groups: '群组',
    silentMode: '静音模式',
    privacy: '隐私',
    readReceipts: '已读回执',
    showReadConfirmations: '显示我何时读取消息',
    onlineStatus: '在线状态',
    showWhenOnline: '显示我何时在线',
    profilePhoto: '个人资料照片',
    lastSeen: '最后一次见到',
    multimedia: '多媒体',
    autoDownload: '自动下载',
    saveToGallery: '保存到相册',
    appearance: '外观',
    theme: '主题',

    // Themes
    defaultTheme: '默认',
    oceanTheme: '海洋',
    sunsetTheme: '日落',
    forestTheme: '森林',
    purpleTheme: '紫色',

    // Language
    language: '语言',
    selectLanguage: '选择语言',

    // Account
    accountActions: '账户操作',
    clearHistory: '清除所有历史记录',
    logout: '登出',

    // App info
    poweredBy: '基于 Nostr',
    timings: '⏱ ⏱ ⏱',
    viewNips: '查看 NIPs',

    // Status
    loading: '加载中...',
    connecting: '连接中...',
    connected: '已连接',
    error: '错误',
    retry: '重试',
    cancel: '取消',
    save: '保存',
    apply: '应用',
    yes: '是',
    no: '否',
    ok: '确定',

    // Time
    justNow: '刚刚',
    minuteAgo: '1 分钟前',
    minutesAgo: '{{count}} 分钟前',
    hourAgo: '1 小时前',
    hoursAgo: '{{count}} 小时前',
    dayAgo: '1 天前',
    daysAgo: '{{count}} 天前',

    // Voice messages
    voiceMessage: '语音消息',
    holdToRecord: '按住录音',
    releaseToSend: '松开发送',

    // File attachments
    attachFile: '附加文件',
    photo: '照片',
    video: '视频',
    document: '文档',
    location: '位置',

    // Error messages
    somethingWentWrong: '出了些问题',
    errorReported: '错误已报告',
    reloadPage: '重新加载页面',
    networkError: '网络错误',
    tryAgain: '重试',

    // Notifications
    requestNotificationPermission: '请求通知权限',
    notificationGranted: '已授权',
    notificationDenied: '已阻止',
    notificationDefault: '未请求',
    testNotification: '测试通知',
    newContactRequest: '新的联系人请求',
    newMessage: '{{name}}: {{message}}',

    about: '关于',
    aboutDescription: 'MensaG 是基于 Nostr 协议构建的私有消息应用。',
    version: '版本',
    features: '功能',
    secure: '安全',
    private: '私有',
    decentralized: '去中心化',
    lightning: '闪电网络',
    crypto: '加密货币',
  },
  ja: {
    // Navigation
    chats: 'チャット',
    profile: 'プロフィール',
    settings: '設定',
    back: '戻る',

    // Landing page
    welcome: 'MensaG へようこそ',
    secureMessaging: 'Nostr を使用したプライベートメッセージ',
    connectFirst: 'アカウントを作成するには 먼저 Nostr に接続してください',
    privateEncryption: 'NIP-17 を使用したエンドツーエンド暗号化',

    // Setup
    nicknameSetup: 'ニックネームを設定',
    nicknameRequired: 'あなたのニックネーム (MensaG 上でユニーク) *',
    nicknamePlaceholder: '例: user123, anonymous456 (最大 29 文字、英数字のみ)',
    nicknameHelp: 'これは MensaG 上でのあなたのユニークな識別子になります。他のユーザーはこのニックネームであなたを見つけることができます。',
    nicknameAvailable: '✓ ニックネーム利用可能',
    nicknameTaken: 'このニックネームは既に使用されています',
    nicknameInvalid: '無効なニックネーム',
    nicknameTooLong: 'ニックネームは最大 29 文字まで',
    nicknameOnlyChars: '英字と数字のみが許可されています',
    continueToMensag: 'MensaG へ進む',
    updating: '更新中...',

    // Chats
    searchChats: '会話を検索...',
    addContact: '連絡先を追加',
    noChats: '会話がありません',
    noChatsDescription: '新しい会話を開始するには連絡先を追加し、そのニックネームを使用してください',
    addAContact: '連絡先を追加',
    unreadMessages: '未読',

    // Contacts
    findUser: 'ユーザーを検索',
    enterNickname: 'ニックネームを入力 (例: user123)',
    userFoundOn: 'ユニークなニックネームで MensaG ユーザーを検索',
    searching: '検索中...',
    userNotFound: 'ユーザーが見つかりません',
    userNotFoundDesc: 'ニックネームを確認してもう一度試してください',
    asMensagUser: 'MensaG ユーザーとして',
    invitationMessage: '招待メッセージ (オプション)',
    invitationPlaceholder: 'こんにちは！あなたを連絡先として追加したいと思います...',
    charactersRemaining: '残りの文字数',
    sendRequest: '連絡先リクエストを送信',
    sending: '送信中...',

    // Contact Requests
    contactRequests: '連絡先リクエスト',
    receivedRequests: '受信したリクエスト',
    sentRequests: '送信したリクエスト',
    accept: '承認',
    decline: '拒否',

    // Messages
    writeMessage: 'メッセージを書く...',
    sendMessage: 'メッセージを送信',
    messageSent: 'メッセージが送信されました',
    messageDelivered: 'メッセージが配信されました',
    messageRead: 'メッセージが読み取られました',
    messageSending: '送信中...',
    startChat: '最初のメッセージを送信してチャットを開始',
    online: 'オンライン',
    offline: 'オフライン',
    lastSeen: '最後に見た',

    // Profile
    profileInfo: 'プロフィール情報',
    editProfile: 'プロフィールを編集',
    uniqueNickname: 'ユニークなニックネーム',
    changeNick: 'ニックネームを変更',
    connected: '接続済み',
    memberSince: 'メンバー期間',
    changeNickname: 'ニックネームを変更',

    // Settings
    notifications: '通知',
    pushNotifications: 'プッシュ通知',
    messages: 'メッセージ',
    calls: '通話',
    groups: 'グループ',
    silentMode: 'サイレントモード',
    privacy: 'プライバシー',
    readReceipts: '既読確認',
    showReadConfirmations: 'メッセージを読むタイミングを表示',
    onlineStatus: 'オンラインステータス',
    showWhenOnline: 'オンライン時の表示',
    profilePhoto: 'プロフィール写真',
    lastSeen: '最後に見た',
    multimedia: 'マルチメディア',
    autoDownload: '自動ダウンロード',
    saveToGallery: 'ギャラリーに保存',
    appearance: '外観',
    theme: 'テーマ',

    // Themes
    defaultTheme: 'デフォルト',
    oceanTheme: 'オーシャン',
    sunsetTheme: 'サンセット',
    forestTheme: 'フォレスト',
    purpleTheme: 'パープル',

    // Language
    language: '言語',
    selectLanguage: '言語を選択',

    // Account
    accountActions: 'アカウントアクション',
    clearHistory: 'すべての履歴をクリア',
    logout: 'ログアウト',

    // App info
    poweredBy: 'Nostr が提供',
    timings: '⏱ ⏱ ⏱',
    viewNips: 'NIPs を表示',

    // Status
    loading: '読み込み中...',
    connecting: '接続中...',
    connected: '接続済み',
    error: 'エラー',
    retry: '再試行',
    cancel: 'キャンセル',
    save: '保存',
    apply: '適用',
    yes: 'はい',
    no: 'いいえ',
    ok: 'OK',

    // Time
    justNow: 'たった今',
    minuteAgo: '1 分前',
    minutesAgo: '{{count}} 分前',
    hourAgo: '1 時間前',
    hoursAgo: '{{count}} 時間前',
    dayAgo: '1 日前',
    daysAgo: '{{count}} 日前',

    // Voice messages
    voiceMessage: 'ボイスメッセージ',
    holdToRecord: '長押しして録音',
    releaseToSend: '離して送信',

    // File attachments
    attachFile: 'ファイルを添付',
    photo: '写真',
    video: '動画',
    document: 'ドキュメント',
    location: '位置',

    // Error messages
    somethingWentWrong: '問題が発生しました',
    errorReported: 'エラーが報告されました',
    reloadPage: 'ページをリロード',
    networkError: 'ネットワークエラー',
    tryAgain: 'もう一度試す',

    // Notifications
    requestNotificationPermission: '通知許可をリクエスト',
    notificationGranted: '許可済み',
    notificationDenied: 'ブロック済み',
    notificationDefault: '未リクエスト',
    testNotification: '通知をテスト',
    newContactRequest: '新しい連絡先リクエスト',
    newMessage: '{{name}}: {{message}}',

    about: 'について',
    aboutDescription: 'MensaG は Nostr プロトコルに基づいたプライベートメッセージングアプリケーションです。',
    version: 'バージョン',
    features: '機能',
    secure: 'セキュア',
    private: 'プライベート',
    decentralized: '分散型',
    lightning: 'ライトニング',
    crypto: 'クリプト',
  },
  ko: {
    // Navigation
    chats: '채팅',
    profile: '프로필',
    settings: '설정',
    back: '뒤로',

    // Landing page
    welcome: 'MensaG 에 오신 것을 환영합니다',
    secureMessaging: 'Nostr 를 사용한 개인 메시지',
    connectFirst: '계정을 만들려면 먼저 Nostr 에 연결하세요',
    privateEncryption: 'NIP-17 을 사용한 종단간 암호화',

    // Setup
    nicknameSetup: '닉네임 설정',
    nicknameRequired: '귀하의 닉네임 (MensaG 에서 고유) *',
    nicknamePlaceholder: '예: user123, anonymous456 (최대 29자, 영문과 숫자만)',
    nicknameHelp: '이는 MensaG 에서 귀하의 고유 식별자가 됩니다. 다른 사용자가 이 닉네임으로 귀하를 찾을 수 있습니다.',
    nicknameAvailable: '✓ 닉네임 사용 가능',
    nicknameTaken: '이 닉네임은 이미 사용 중입니다',
    nicknameInvalid: '유효하지 않은 닉네임',
    nicknameTooLong: '닉네임은 최대 29자까지 가능합니다',
    nicknameOnlyChars: '영문과 숫자만 허용됩니다',
    continueToMensag: 'MensaG 로 계속',
    updating: '업데이트 중...',

    // Chats
    searchChats: '대화 검색...',
    addContact: '연락처 추가',
    noChats: '대화가 없습니다',
    noChatsDescription: '새 대화를 시작하려면 연락처를 추가하고 닉네임을 사용하세요',
    addAContact: '연락처 추가',
    unreadMessages: '읽지 않음',

    // Contacts
    findUser: '사용자 찾기',
    enterNickname: '닉네임을 입력하세요 (예: user123)',
    userFoundOn: '고유 닉네임을 통해 MensaG 사용자 검색',
    searching: '검색 중...',
    userNotFound: '사용자를 찾을 수 없습니다',
    userNotFoundDesc: '닉네임을 확인하고 다시 시도하세요',
    asMensagUser: 'MensaG 사용자로',
    invitationMessage: '초대 메시지 (선택사항)',
    invitationPlaceholder: '안녕하세요! 귀하를 연락처로 추가하고 싶습니다...',
    charactersRemaining: '남은 문자 수',
    sendRequest: '연락처 요청 보내기',
    sending: '보내는 중...',

    // Contact Requests
    contactRequests: '연락처 요청',
    receivedRequests: '받은 요청',
    sentRequests: '보낸 요청',
    accept: '수락',
    decline: '거절',

    // Messages
    writeMessage: '메시지를 작성하세요...',
    sendMessage: '메시지 보내기',
    messageSent: '메시지가 전송되었습니다',
    messageDelivered: '메시지가 전달되었습니다',
    messageRead: '메시지가 읽혔습니다',
    messageSending: '보내는 중...',
    startChat: '첫 메시지를 보내어 채팅을 시작하세요',
    online: '온라인',
    offline: '오프라인',
    lastSeen: '마지막으로 본',

    // Profile
    profileInfo: '프로필 정보',
    editProfile: '프로필 편집',
    uniqueNickname: '고유 닉네임',
    changeNick: '닉네임 변경',
    connected: '연결됨',
    memberSince: '회원 기간',
    changeNickname: '닉네임 변경',

    // Settings
    notifications: '알림',
    pushNotifications: '푸시 알림',
    messages: '메시지',
    calls: '통화',
    groups: '그룹',
    silentMode: '무음 모드',
    privacy: '개인정보 보호',
    readReceipts: '읽음 확인',
    showReadConfirmations: '메시지를 읽을 때 표시',
    onlineStatus: '온라인 상태',
    showWhenOnline: '온라인일 때 표시',
    profilePhoto: '프로필 사진',
    lastSeen: '마지막으로 본',
    multimedia: '멀티미디어',
    autoDownload: '자동 다운로드',
    saveToGallery: '갤러리에 저장',
    appearance: '모양',
    theme: '테마',

    // Themes
    defaultTheme: '기본',
    oceanTheme: '바다',
    sunsetTheme: '일몰',
    forestTheme: '숲',
    purpleTheme: '보라',

    // Language
    language: '언어',
    selectLanguage: '언어 선택',

    // Account
    accountActions: '계정 작업',
    clearHistory: '모든 기록 삭제',
    logout: '로그아웃',

    // App info
    poweredBy: 'Nostr 기반',
    timings: '⏱ ⏱ ⏱',
    viewNips: 'NIP 보기',

    // Status
    loading: '로딩 중...',
    connecting: '연결 중...',
    connected: '연결됨',
    error: '오류',
    retry: '재시도',
    cancel: '취소',
    save: '저장',
    apply: '적용',
    yes: '예',
    no: '아니요',
    ok: '확인',

    // Time
    justNow: '방금 전',
    minuteAgo: '1분 전',
    minutesAgo: '{{count}}분 전',
    hourAgo: '1시간 전',
    hoursAgo: '{{count}}시간 전',
    dayAgo: '1일 전',
    daysAgo: '{{count}}일 전',

    // Voice messages
    voiceMessage: '음성 메시지',
    holdToRecord: '길게 눌러 녹음',
    releaseToSend: '놓아 보내기',

    // File attachments
    attachFile: '파일 첨부',
    photo: '사진',
    video: '비디오',
    document: '문서',
    location: '위치',

    // Error messages
    somethingWentWrong: '문제가 발생했습니다',
    errorReported: '오류가 보고되었습니다',
    reloadPage: '페이지 새로 고침',
    networkError: '네트워크 오류',
    tryAgain: '다시 시도',

    // Notifications
    requestNotificationPermission: '알림 권한 요청',
    notificationGranted: '허용됨',
    notificationDenied: '차단됨',
    notificationDefault: '요청되지 않음',
    testNotification: '알림 테스트',
    newContactRequest: '새 연락처 요청',
    newMessage: '{{name}}: {{message}}',

    about: '소개',
    aboutDescription: 'MensaG 는 Nostr 프로토콜에 기반한 개인 메시징 애플리케이션입니다.',
    version: '버전',
    features: '기능',
    secure: '보안',
    private: '개인',
    decentralized: '분산',
    lightning: '라이트닝',
    crypto: '암호화폐',
  },
  ru: {
    // Navigation
    chats: 'Чаты',
    profile: 'Профиль',
    settings: 'Настройки',
    back: 'Назад',

    // Landing page
    welcome: 'Добро пожаловать в MensaG',
    secureMessaging: 'Приватные сообщения с Nostr',
    connectFirst: 'Сначала подключитесь к Nostr, чтобы создать учетную запись',
    privateEncryption: 'Сквозное шифрование с NIP-17',

    // Setup
    nicknameSetup: 'Настройте свой Ник',
    nicknameRequired: 'Ваш Ник (уникальный на MensaG) *',
    nicknamePlaceholder: 'Напр: user123, anonymous456 (макс 29 символов, только буквы и цифры)',
    nicknameHelp: 'Это будет вашим уникальным идентификатором на MensaG. Другие пользователи найдут вас по этому нику.',
    nicknameAvailable: '✓ Ник доступен',
    nicknameTaken: 'Этот ник уже занят',
    nicknameInvalid: 'Неверный ник',
    nicknameTooLong: 'Ник должен быть максимум 29 символов',
    nicknameOnlyChars: 'Разрешены только буквы и цифры',
    continueToMensag: 'Продолжить с MensaG',
    updating: 'Обновление...',

    // Chats
    searchChats: 'Поиск чатов...',
    addContact: 'Добавить Контакт',
    noChats: 'У вас нет чатов',
    noChatsDescription: 'Начните новый чат, добавив контакт с использованием его никнейма',
    addAContact: 'Добавить контакт',
    unreadMessages: 'Непрочитано',

    // Contacts
    findUser: 'Найти пользователя',
    enterNickname: 'Введите никнейм (напр: user123)',
    userFoundOn: 'Поиск пользователей MensaG по их уникальному никнейму',
    searching: 'Поиск...',
    userNotFound: 'Пользователь не найден',
    userNotFoundDesc: 'Проверьте никнейм и попробуйте снова',
    asMensagUser: 'как пользователь MensaG',
    invitationMessage: 'Приглашение (опционально)',
    invitationPlaceholder: 'Привет! Я хочу добавить вас как контакт...',
    charactersRemaining: 'осталось символов',
    sendRequest: 'Отправить запрос контакта',
    sending: 'Отправка...',

    // Contact Requests
    contactRequests: 'Запросы Контактов',
    receivedRequests: 'Полученные Запросы',
    sentRequests: 'Отправленные Запросы',
    accept: 'Принять',
    decline: 'Отклонить',

    // Messages
    writeMessage: 'Напишите сообщение...',
    sendMessage: 'Отправить сообщение',
    messageSent: 'Сообщение отправлено',
    messageDelivered: 'Сообщение доставлено',
    messageRead: 'Сообщение прочитано',
    messageSending: 'Отправка...',
    startChat: 'Отправьте первое сообщение, чтобы начать чат',
    online: 'Онлайн',
    offline: 'Не в сети',
    lastSeen: 'Последний раз видел',

    // Profile
    profileInfo: 'Информация Профиля',
    editProfile: 'Редактировать Профиль',
    uniqueNickname: 'Уникальный никнейм',
    changeNick: 'Изменить ник',
    connected: 'Подключен',
    memberSince: 'Участник с',
    changeNickname: 'Изменить никнейм',

    // Settings
    notifications: 'Уведомления',
    pushNotifications: 'Push-уведомления',
    messages: 'Сообщения',
    calls: 'Звонки',
    groups: 'Группы',
    silentMode: 'Беззвучный режим',
    privacy: 'Конфиденциальность',
    readReceipts: 'Квитанции о прочтении',
    showReadConfirmations: 'Показывать, когда я читаю сообщения',
    onlineStatus: 'Статус онлайн',
    showWhenOnline: 'Показывать, когда я онлайн',
    profilePhoto: 'Фото профиля',
    lastSeen: 'Последний раз видел',
    multimedia: 'Мультимедиа',
    autoDownload: 'Автоматическая загрузка',
    saveToGallery: 'Сохранить в галерею',
    appearance: 'Внешний вид',
    theme: 'Тема',

    // Themes
    defaultTheme: 'По умолчанию',
    oceanTheme: 'Океан',
    sunsetTheme: 'Закат',
    forestTheme: 'Лес',
    purpleTheme: 'Фиолетовый',

    // Language
    language: 'Язык',
    selectLanguage: 'Выбрать язык',

    // Account
    accountActions: 'Действия Аккаунта',
    clearHistory: 'Очистить всю историю',
    logout: 'Выйти',

    // App info
    poweredBy: 'Работает на Nostr',
    timings: '⏱ ⏱ ⏱',
    viewNips: 'Просмотреть NIPs',

    // Status
    loading: 'Загрузка...',
    connecting: 'Подключение...',
    connected: 'Подключен',
    error: 'Ошибка',
    retry: 'Повторить',
    cancel: 'Отмена',
    save: 'Сохранить',
    apply: 'Применить',
    yes: 'Да',
    no: 'Нет',
    ok: 'OK',

    // Time
    justNow: 'Только что',
    minuteAgo: '1 минуту назад',
    minutesAgo: '{{count}} минут назад',
    hourAgo: '1 час назад',
    hoursAgo: '{{count}} часов назад',
    dayAgo: '1 день назад',
    daysAgo: '{{count}} дней назад',

    // Voice messages
    voiceMessage: 'Голосовое сообщение',
    holdToRecord: 'Удерживать для записи',
    releaseToSend: 'Отпустить для отправки',

    // File attachments
    attachFile: 'Прикрепить файл',
    photo: 'Фото',
    video: 'Видео',
    document: 'Документ',
    location: 'Местоположение',

    // Error messages
    somethingWentWrong: 'Что-то пошло не так',
    errorReported: 'Ошибка была сообщена',
    reloadPage: 'Перезагрузить страницу',
    networkError: 'Ошибка сети',
    tryAgain: 'Попробовать снова',

    // Notifications
    requestNotificationPermission: 'Запросить разрешения на уведомления',
    notificationGranted: 'Предоставлены',
    notificationDenied: 'Заблокированы',
    notificationDefault: 'Не запрошены',
    testNotification: 'Тест уведомления',
    newContactRequest: 'Новый запрос контакта',
    newMessage: '{{name}}: {{message}}',

    about: 'О программе',
    aboutDescription: 'MensaG - приложение для приватных сообщений, основанное на протоколе Nostr.',
    version: 'Версия',
    features: 'Функции',
    secure: 'Безопасный',
    private: 'Приватный',
    decentralized: 'Децентрализованный',
    lightning: 'Lightning',
    crypto: 'Крипто',
  },
};

// Hook para usar traducciones
export function useTranslation() {
  const [currentLanguage, setCurrentLanguage] = useState<Language>('es');

  // Detectar idioma del navegador al cargar
  useEffect(() => {
    const browserLang = navigator.language.split('-')[0] as Language;
    if (languages[browserLang]) {
      setCurrentLanguage(browserLang);
    } else {
      // Fallback a inglés si no soportamos el idioma del navegador
      setCurrentLanguage('en');
    }
  }, []);

  // Función para traducciones anidadas (objeto.traducción)
  const t = (key: string, fallback?: string): string => {
    const keys = key.split('.');
    let value: any = translations[currentLanguage];

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        break;
      }
    }

    if (typeof value === 'string') {
      return value;
    }

    // Fallback a inglés si no existe traducción
    if (currentLanguage !== 'en') {
      let englishValue: any = translations.en;
      for (const k of keys) {
        if (englishValue && typeof englishValue === 'object' && k in englishValue) {
          englishValue = englishValue[k];
        } else {
          break;
        }
      }
      if (typeof englishValue === 'string') {
        return englishValue;
      }
    }

    // Último fallback proporcionado o la key
    return fallback || key;
  };

  return {
    t,
    currentLanguage,
    setLanguage: setCurrentLanguage,
    languages,
  };
}

// Context provider para el idioma
const TranslationContext = createContext<ReturnType<typeof useTranslation> | null>(null);

interface TranslationProviderProps {
  children: ReactNode;
}

export function TranslationProvider({ children }: TranslationProviderProps) {
  const translation = useTranslation();

  return (
    <TranslationContext.Provider value={translation}>
      {children}
    </TranslationContext.Provider>
  );
}

export function useTranslationContext() {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslationContext must be used within TranslationProvider');
  }
  return context;
}